import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  InputAdornment,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Google,
  GitHub,
  Apple,
} from "@mui/icons-material";
import {
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
} from "../firebase";

interface SignInProps {
  onSignIn: (name: string) => void;
  darkMode: boolean;
}

export default function SignIn({
  onSignIn,
  darkMode,
}: SignInProps): JSX.Element {
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        const user = await signUpWithEmail(email, password);
        if (user) onSignIn(user.email?.split("@")[0] || "User");
      } else {
        const user = await signInWithEmail(email, password);
        if (user) onSignIn(user.email?.split("@")[0] || "User");
      }
    } catch (err: any) {
      console.error("Email Auth Error:", err);
      // Translate Firebase error codes to readable messages
      if (err.code === "auth/email-already-in-use") {
        setError("This email is already in use.");
      } else if (
        err.code === "auth/invalid-credential" ||
        err.code === "auth/wrong-password"
      ) {
        setError("Invalid email or password.");
      } else if (err.code === "auth/weak-password") {
        setError("Password should be at least 6 characters.");
      } else {
        setError(err.message || "Authentication failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      if (user) {
        // Extract name from email or use displayName
        const userName =
          user.displayName || user.email?.split("@")[0] || "User";
        onSignIn(userName);
      }
    } catch (err: any) {
      // User closed the popup — this is intentional, not an error
      if (
        err.code === "auth/popup-closed-by-user" ||
        err.code === "auth/cancelled-popup-request"
      ) {
        setLoading(false);
        return;
      }
      console.error("Google Sign-In Error:", err);
      setError(
        err.message || "Failed to sign in with Google. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: { xs: 4, sm: 6 },
          borderRadius: "32px",
          bgcolor: darkMode ? "#0a0a0a" : "#ffffff",
          border: "1px solid",
          borderColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
          textAlign: "center",
          boxShadow: darkMode ? "none" : "0 20px 60px -20px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="/images/logo-tab.png"
            sx={{ width: 80, height: 80, borderRadius: "16px", mb: 2 }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: darkMode ? "white" : "black",
              mb: 1,
              trackingTight: "-0.04em",
            }}
          >
            {isSignUp ? "Create an Account" : "Welcome Back"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: darkMode ? "#4b5563" : "#64748b" }}
          >
            {isSignUp
              ? "Enter your details to create a new account."
              : "Enter your credentials to access your academic tools."}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Email address"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputLabelProps={{
                sx: {
                  color: darkMode ? "#4b5563" : "#64748b",
                  "&.Mui-focused": { color: darkMode ? "white" : "#3b82f6" },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: darkMode ? "white" : "black",
                  borderRadius: "14px",
                  bgcolor: darkMode
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
                  "& fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.1)",
                  },
                  "&:hover fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: darkMode ? "white" : "#3b82f6",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputLabelProps={{
                sx: {
                  color: darkMode ? "#4b5563" : "#64748b",
                  "&.Mui-focused": { color: darkMode ? "white" : "#3b82f6" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: "#4b5563" }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: darkMode ? "white" : "black",
                  borderRadius: "14px",
                  bgcolor: darkMode
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(0,0,0,0.02)",
                  "& fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.05)"
                      : "rgba(0,0,0,0.1)",
                  },
                  "&:hover fieldset": {
                    borderColor: darkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: darkMode ? "white" : "#3b82f6",
                  },
                },
              }}
            />

            {!isSignUp && (
              <Box sx={{ textAlign: "right" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#3b82f6", fontWeight: 600, cursor: "pointer" }}
                >
                  Forgot password?
                </Typography>
              </Box>
            )}

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.8,
                borderRadius: "14px",
                bgcolor: darkMode ? "white" : "black",
                color: darkMode ? "black" : "white",
                fontWeight: 700,
                fontSize: "15px",
                textTransform: "none",
                "&:hover": { bgcolor: darkMode ? "#e5e7eb" : "#1f2937" },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isSignUp ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </Button>
          </Stack>
        </form>

        <Box sx={{ my: 4, position: "relative" }}>
          <Divider
            sx={{
              borderColor: darkMode
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.05)",
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: darkMode ? "#0a0a0a" : "#ffffff",
              px: 2,
              color: darkMode ? "#4b5563" : "#64748b",
            }}
          >
            OR CONTINUE WITH
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <Google />}
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{
              borderRadius: "14px",
              color: darkMode ? "white" : "black",
              borderColor: darkMode
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.1)",
              textTransform: "none",
              py: 1.2,
              "&:hover": {
                bgcolor: darkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.02)",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.2)",
              },
            }}
          >
            {loading ? "Signing in..." : "Google"}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Apple />}
            sx={{
              borderRadius: "14px",
              color: darkMode ? "white" : "black",
              borderColor: darkMode
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.1)",
              textTransform: "none",
              py: 1.2,
              "&:hover": {
                bgcolor: darkMode
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.02)",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.2)",
              },
            }}
          >
            Apple
          </Button>
        </Stack>

        <Typography
          variant="body2"
          sx={{ mt: 4, color: darkMode ? "#4b5563" : "#64748b" }}
        >
          {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
          <span
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError(null);
            }}
            style={{
              color: darkMode ? "white" : "#3b82f6",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {isSignUp ? "Sign In" : "Create Account"}
          </span>
        </Typography>
      </Paper>
    </Box>
  );
}
